/**
 * 全局搜索区域折叠：自动增强 .filterToolbar 内联查询表单
 * - 默认折叠为一行，最小高度
 * - 查询/重置按钮始终固定在右侧末尾
 * - 已手动实现「更多搜索/折叠」的页面自动跳过
 *
 * 禁用：在容器上添加 data-no-filter-collapse
 */

const MANUAL_COLLAPSE_TEXTS = ['更多搜索', '折叠']
const TOGGLE_CLASS = 'filter-auto-collapse-toggle'
const EXTRA_CLASS = 'filter-auto-collapse-extra'
const toolbarRegistry = new WeakMap()
const LAYOUT_CHANGE_EVENT = 'filter-toolbar-layout-change'

function notifyLayoutChange() {
  window.dispatchEvent(new Event(LAYOUT_CHANGE_EVENT))
}

function hasManualCollapse(toolbar) {
  if (toolbar.hasAttribute('data-no-filter-collapse')) {
    return true
  }
  const buttons = toolbar.querySelectorAll('.el-button:not(.' + TOGGLE_CLASS + ')')
  for (let i = 0; i < buttons.length; i++) {
    const text = (buttons[i].textContent || '').trim()
    if (MANUAL_COLLAPSE_TEXTS.indexOf(text) !== -1) {
      return true
    }
  }
  return toolbar.querySelector('.el-collapse-transition') != null
}

function resolveToolbarNodes(rootEl) {
  const nodes = []
  if (!rootEl || !rootEl.querySelectorAll) {
    return nodes
  }

  rootEl.querySelectorAll('.filterToolbar').forEach(function (el) {
    if (el.classList.contains('el-form')) {
      nodes.push({ toolbar: el, form: el })
      return
    }
    const form = el.querySelector(':scope > .el-form') || el.querySelector('.el-form')
    if (form) {
      nodes.push({ toolbar: el, form: form })
    }
  })

  return nodes
}

function findActionsFormItem(form) {
  const directItems = Array.from(form.children).filter(function (child) {
    return child.classList && child.classList.contains('el-form-item')
  })

  for (let i = 0; i < directItems.length; i++) {
    const item = directItems[i]
    if (item.classList.contains('filter-auto-collapse-actions')) {
      return item
    }
  }

  for (let i = directItems.length - 1; i >= 0; i--) {
    const item = directItems[i]
    const text = item.textContent || ''
    if (
      item.querySelector('.el-icon-search') ||
      text.indexOf('查询') !== -1 ||
      text.indexOf('搜索') !== -1 ||
      text.indexOf('重置') !== -1
    ) {
      return item
    }
  }

  return directItems.length ? directItems[directItems.length - 1] : null
}

function getFieldNodes(form, actionsItem) {
  return Array.from(form.children).filter(function (child) {
    if (!child.classList) {
      return false
    }
    if (child === actionsItem) {
      return false
    }
    return (
      child.classList.contains('el-form-item') ||
      child.tagName === 'SPAN' ||
      child.tagName === 'DIV'
    )
  })
}

function clearExtraNodes(nodes) {
  nodes.forEach(function (node) {
    node.classList.remove(EXTRA_CLASS)
  })
}

var COLLAPSED_VPAD = 6

function measureCollapsedContentHeight(toolbar, form) {
  if (toolbar !== form) {
    return form.offsetHeight
  }

  // el-form 同时带 filterToolbar 时 toolbar === form；
  // 在已设置 --filter-toolbar-height 的元素上读 offsetHeight 会触发 ResizeObserver 循环增高
  const prevHeightVar = toolbar.style.getPropertyValue('--filter-toolbar-height')
  toolbar.style.removeProperty('--filter-toolbar-height')
  const height = form.scrollHeight
  if (prevHeightVar) {
    toolbar.style.setProperty('--filter-toolbar-height', prevHeightVar)
  }
  return height
}

function syncCollapsedRows(toolbar, form, actionsItem) {
  const fieldNodes = getFieldNodes(form, actionsItem)

  if (!toolbar.classList.contains('is-collapsed')) {
    clearExtraNodes(fieldNodes)
    toolbar.style.removeProperty('--filter-toolbar-height')
    notifyLayoutChange()
    return
  }

  clearExtraNodes(fieldNodes)

  if (!fieldNodes.length) {
    toolbar.style.removeProperty('--filter-toolbar-height')
    return
  }

  let firstRowTop = null
  fieldNodes.forEach(function (node) {
    const top = node.offsetTop
    if (firstRowTop === null || top < firstRowTop) {
      firstRowTop = top
    }
  })

  fieldNodes.forEach(function (node) {
    const top = node.offsetTop
    if (Math.abs(top - firstRowTop) <= 2) {
      node.classList.remove(EXTRA_CLASS)
      return
    }
    node.classList.add(EXTRA_CLASS)
  })

  const measuredHeight = measureCollapsedContentHeight(toolbar, form)
  if (measuredHeight > 0) {
    const extraPad = toolbar === form ? 0 : COLLAPSED_VPAD * 2
    const nextHeight = measuredHeight + extraPad + 'px'
    const currentHeight = toolbar.style.getPropertyValue('--filter-toolbar-height')
    if (currentHeight !== nextHeight) {
      toolbar.style.setProperty('--filter-toolbar-height', nextHeight)
    }
  }

  notifyLayoutChange()
}

function updateToggleLabel(toolbar, toggle) {
  const collapsed = toolbar.classList.contains('is-collapsed')
  toggle.innerHTML = collapsed
    ? '<i class="el-icon-arrow-down"></i><span>展开</span>'
    : '<i class="el-icon-arrow-up"></i><span>折叠</span>'
}

function ensureResizeObserver(toolbar, form, actionsItem) {
  if (typeof ResizeObserver === 'undefined') {
    return
  }

  let entry = toolbarRegistry.get(toolbar)
  if (entry && entry.observer) {
    return
  }

  const observer = new ResizeObserver(function () {
    if (toolbar.classList.contains('is-collapsed')) {
      syncCollapsedRows(toolbar, form, actionsItem)
    }
  })

  observer.observe(toolbar)
  observer.observe(form)
  toolbarRegistry.set(toolbar, { observer: observer, form: form, actionsItem: actionsItem })
}

function ensureToggleButton(toolbar, form, actionsItem) {
  let toggle = toolbar.querySelector('.' + TOGGLE_CLASS)
  if (!toggle) {
    toggle = document.createElement('button')
    toggle.type = 'button'
    toggle.className = TOGGLE_CLASS + ' el-button el-button--default el-button--small'
    toggle.addEventListener('click', function (event) {
      event.preventDefault()
      event.stopPropagation()
      toolbar.classList.toggle('is-collapsed')
      toolbar.setAttribute(
        'data-filter-collapse',
        toolbar.classList.contains('is-collapsed') ? 'collapsed' : 'expanded'
      )
      syncCollapsedRows(toolbar, form, actionsItem)
      updateToggleLabel(toolbar, toggle)
      notifyLayoutChange()
      window.dispatchEvent(new Event('resize'))
    })
  }

  const content = actionsItem.querySelector('.el-form-item__content')
  if (content && !content.contains(toggle)) {
    content.insertBefore(toggle, content.firstChild)
  }

  updateToggleLabel(toolbar, toggle)
  return toggle
}

function enhanceToolbar(toolbar, form) {
  if (toolbar.getAttribute('data-filter-collapse') === 'manual') {
    return
  }
  if (hasManualCollapse(toolbar)) {
    toolbar.setAttribute('data-filter-collapse', 'manual')
    return
  }

  const actionsItem = findActionsFormItem(form)
  if (!actionsItem) {
    return
  }

  toolbar.classList.add('filter-auto-collapse')
  const collapseState = toolbar.getAttribute('data-filter-collapse')
  if (collapseState === 'expanded') {
    toolbar.classList.remove('is-collapsed')
  } else if (!toolbar.classList.contains('is-collapsed')) {
    toolbar.classList.add('is-collapsed')
    toolbar.setAttribute('data-filter-collapse', 'collapsed')
  }

  actionsItem.classList.add('filter-auto-collapse-actions')
  ensureToggleButton(toolbar, form, actionsItem)
  ensureResizeObserver(toolbar, form, actionsItem)

  requestAnimationFrame(function () {
    syncCollapsedRows(toolbar, form, actionsItem)
  })
}

export function applyFilterToolbarEnhancement(rootEl) {
  resolveToolbarNodes(rootEl).forEach(function (node) {
    enhanceToolbar(node.toolbar, node.form)
  })
}

export function installFilterToolbarCollapse(Vue) {
  var syncAll = function () {
    var vm = this
    if (!vm || !vm.$nextTick) {
      return
    }
    vm.$nextTick(function () {
      if (vm.$el && vm.$el.querySelector && vm.$el.querySelector('.filterToolbar')) {
        applyFilterToolbarEnhancement(vm.$el)
      }
    })
  }

  Vue.mixin({
    mounted: syncAll,
    updated: syncAll,
    activated: syncAll
  })

  window.addEventListener('resize', function () {
    document.querySelectorAll('.filter-auto-collapse.is-collapsed').forEach(function (toolbar) {
      const entry = toolbarRegistry.get(toolbar)
      if (entry) {
        syncCollapsedRows(toolbar, entry.form, entry.actionsItem)
      }
    })
  }, { passive: true })
}
